import { useState, useMemo } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Users, LayoutGrid, List, Search, Edit2, X, Check, Eye, BookOpen,
    TrendingUp, Calendar, Mail, Phone, Award, Target, Clock, Flame,
    ArrowUpRight, ArrowDownRight, ChevronRight, Plus, Trash2, AlertTriangle
} from 'lucide-react'
import { mockMentorStudents, type MentorStudent } from '@/data/mentor-mock-data'
import { mockAdminCourses } from '@/data/admin-mock-data'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'

type ViewMode = 'cards' | 'table'
type DetailTab = 'profile' | 'courses' | 'performance' | 'activity'

export function AdminStudentsView() {
    const [viewMode, setViewMode] = useState<ViewMode>('table')
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedBatch, setSelectedBatch] = useState<string>('all')
    const [students, setStudents] = useState(mockMentorStudents)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editForm, setEditForm] = useState<{ batch: string }>({ batch: '' })
    const [selectedStudent, setSelectedStudent] = useState<MentorStudent | null>(null)
    const [detailTab, setDetailTab] = useState<DetailTab>('profile')
    const [studentCourses, setStudentCourses] = useState<Record<string, string[]>>({})
    const [showAddCourse, setShowAddCourse] = useState(false)

    // Initialize student courses from mock data
    const getStudentCourses = (studentId: string) => {
        if (studentCourses[studentId]) return studentCourses[studentId]
        const student = students.find(s => s.id === studentId)
        if (student?.courseId) return [student.courseId]
        return []
    }

    const allBatches = useMemo(() => {
        const batches = new Set(mockMentorStudents.map(s => s.batch))
        batches.add('NEET 2025 Batch A')
        batches.add('NEET 2025 Batch B')
        batches.add('NEET 2026 Batch A')
        batches.add('Dropper Batch 2025')
        return Array.from(batches).sort()
    }, [])

    const filteredStudents = useMemo(() => {
        return students.filter(student => {
            const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.email?.toLowerCase().includes(searchQuery.toLowerCase())
            const matchesBatch = selectedBatch === 'all' || student.batch === selectedBatch
            return matchesSearch && matchesBatch
        })
    }, [students, searchQuery, selectedBatch])

    const startEditing = (student: MentorStudent) => {
        setEditingId(student.id)
        setEditForm({ batch: student.batch })
    }

    const saveEditing = () => {
        if (!editingId) return
        setStudents(prev => prev.map(s =>
            s.id === editingId ? { ...s, batch: editForm.batch } : s
        ))
        setEditingId(null)
        toast.success('Batch updated successfully')
    }

    const cancelEditing = () => {
        setEditingId(null)
    }

    const openStudentDetail = (student: MentorStudent) => {
        setSelectedStudent(student)
        setDetailTab('profile')
    }

    const handleAddCourse = (courseId: string) => {
        if (!selectedStudent) return
        const current = getStudentCourses(selectedStudent.id)
        if (current.includes(courseId)) {
            toast.error('Course already enrolled')
            return
        }
        setStudentCourses(prev => ({
            ...prev,
            [selectedStudent.id]: [...current, courseId]
        }))
        setShowAddCourse(false)
        toast.success('Course added successfully')
    }

    const handleRemoveCourse = (courseId: string) => {
        if (!selectedStudent) return
        const current = getStudentCourses(selectedStudent.id)
        setStudentCourses(prev => ({
            ...prev,
            [selectedStudent.id]: current.filter(id => id !== courseId)
        }))
        toast.success('Course removed')
    }

    const getTrendIcon = (trend: string) => {
        if (trend.includes('improving')) return <ArrowUpRight className="h-4 w-4 text-emerald-500" />
        if (trend.includes('declining')) return <ArrowDownRight className="h-4 w-4 text-red-500" />
        return <span className="text-slate-500 text-xs font-medium">—</span>
    }

    const getRiskBadge = (risk: string) => {
        switch (risk) {
            case 'low': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">Low Risk</span>
            case 'medium': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Medium Risk</span>
            case 'high': return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">High Risk</span>
            default: return null
        }
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Student Management</h2>
                    <p className="text-sm text-theme-secondary">Manage students, view details, and control course enrollments</p>
                </div>

                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`p-2 rounded-md transition-all ${viewMode === 'cards' ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-blue' : 'text-theme-secondary hover:text-theme-primary'}`}
                    >
                        <LayoutGrid className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => setViewMode('table')}
                        className={`p-2 rounded-md transition-all ${viewMode === 'table' ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-blue' : 'text-theme-secondary hover:text-theme-primary'}`}
                    >
                        <List className="h-5 w-5" />
                    </button>
                </div>
            </div>

            <GlassmorphicCard className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-primary-blue outline-none transition-colors"
                        />
                    </div>

                    <div className="w-full md:w-64">
                        <select
                            value={selectedBatch}
                            onChange={(e) => setSelectedBatch(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 focus:border-primary-blue outline-none transition-colors"
                        >
                            <option value="all">All Batches</option>
                            {allBatches.map(batch => (
                                <option key={batch} value={batch}>{batch}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </GlassmorphicCard>

            {viewMode === 'table' ? (
                <GlassmorphicCard className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                                    <th className="p-4 text-left text-xs font-semibold text-theme-secondary uppercase">Student</th>
                                    <th className="p-4 text-left text-xs font-semibold text-theme-secondary uppercase">Target Year</th>
                                    <th className="p-4 text-left text-xs font-semibold text-theme-secondary uppercase">Batch</th>
                                    <th className="p-4 text-left text-xs font-semibold text-theme-secondary uppercase">Performance</th>
                                    <th className="p-4 text-left text-xs font-semibold text-theme-secondary uppercase hidden md:table-cell">Risk</th>
                                    <th className="p-4 text-right text-xs font-semibold text-theme-secondary uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filteredStudents.map((student) => (
                                    <tr key={student.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-sm">
                                                    {student.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-theme-primary">{student.name}</div>
                                                    <div className="text-xs text-theme-secondary">{student.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-theme-secondary">
                                            NEET {student.targetYear}
                                        </td>
                                        <td className="p-4">
                                            {editingId === student.id ? (
                                                <select
                                                    value={editForm.batch}
                                                    onChange={(e) => setEditForm({ ...editForm, batch: e.target.value })}
                                                    className="w-full max-w-[200px] px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-primary-blue text-sm outline-none"
                                                    autoFocus
                                                >
                                                    {allBatches.map(batch => (
                                                        <option key={batch} value={batch}>{batch}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="inline-flex px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium">
                                                    {student.batch}
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-theme-primary">{student.avgTestScore}</span>
                                                <span className="text-xs text-theme-muted">/ 720</span>
                                                {getTrendIcon(student.performanceTrend)}
                                            </div>
                                        </td>
                                        <td className="p-4 hidden md:table-cell">
                                            {getRiskBadge(student.dropRisk)}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {editingId === student.id ? (
                                                    <>
                                                        <button onClick={saveEditing} className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40 transition-colors" title="Save">
                                                            <Check className="h-4 w-4" />
                                                        </button>
                                                        <button onClick={cancelEditing} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 transition-colors" title="Cancel">
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => openStudentDetail(student)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-blue bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                                                        >
                                                            <Eye className="h-3.5 w-3.5" />
                                                            View
                                                        </button>
                                                        <button onClick={() => startEditing(student)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-secondary hover:text-primary-blue transition-colors" title="Edit Batch">
                                                            <Edit2 className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </GlassmorphicCard>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredStudents.map((student) => (
                        <GlassmorphicCard key={student.id} className="p-5 flex flex-col gap-4">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-lg">
                                        {student.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-theme-primary line-clamp-1">{student.name}</h3>
                                        <p className="text-xs text-theme-secondary">{student.email}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => openStudentDetail(student)} className="p-1.5 text-primary-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded" title="View Details">
                                        <Eye className="h-4 w-4" />
                                    </button>
                                    {editingId === student.id ? (
                                        <>
                                            <button onClick={saveEditing} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check className="h-4 w-4" /></button>
                                            <button onClick={cancelEditing} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><X className="h-4 w-4" /></button>
                                        </>
                                    ) : (
                                        <button onClick={() => startEditing(student)} className="p-1.5 text-theme-secondary hover:text-primary-blue rounded"><Edit2 className="h-4 w-4" /></button>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <div>
                                    <label className="text-xs font-medium text-theme-secondary block mb-1">Batch</label>
                                    {editingId === student.id ? (
                                        <select
                                            value={editForm.batch}
                                            onChange={(e) => setEditForm({ ...editForm, batch: e.target.value })}
                                            className="w-full px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-900 border border-primary-blue text-sm outline-none"
                                        >
                                            {allBatches.map(batch => (
                                                <option key={batch} value={batch}>{batch}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="font-medium text-theme-primary">{student.batch}</div>
                                    )}
                                </div>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="text-xs text-theme-secondary">Target Year</div>
                                        <div className="font-medium text-theme-primary">{student.targetYear}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-theme-secondary">Avg Score</div>
                                        <div className="font-bold text-theme-primary flex items-center gap-1 justify-end">
                                            {student.avgTestScore}
                                            {getTrendIcon(student.performanceTrend)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-2">
                                    {getRiskBadge(student.dropRisk)}
                                    <button
                                        onClick={() => openStudentDetail(student)}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-primary-blue hover:underline"
                                    >
                                        View Details <ChevronRight className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    ))}
                </div>
            )}

            {filteredStudents.length === 0 && (
                <div className="text-center py-12">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-theme-secondary">No students found matching your criteria.</p>
                </div>
            )}

            {/* Student Detail Modal */}
            <AnimatePresence>
                {selectedStudent && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => { setSelectedStudent(null); setShowAddCourse(false) }}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-2xl"
                        >
                            <GlassmorphicCard className="shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
                                {/* Modal Header */}
                                <div className="p-6 border-b border-slate-200 dark:border-slate-700 shrink-0">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-blue to-violet-600 flex items-center justify-center text-white font-bold text-2xl">
                                                {selectedStudent.name.charAt(0)}
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-theme-primary">{selectedStudent.name}</h3>
                                                <p className="text-sm text-theme-secondary">{selectedStudent.email}</p>
                                                <div className="flex items-center gap-2 mt-1.5">
                                                    <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium">{selectedStudent.batch}</span>
                                                    {getRiskBadge(selectedStudent.dropRisk)}
                                                </div>
                                            </div>
                                        </div>
                                        <button onClick={() => { setSelectedStudent(null); setShowAddCourse(false) }} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                            <X className="h-5 w-5 text-theme-secondary" />
                                        </button>
                                    </div>

                                    {/* Detail Tabs */}
                                    <div className="flex items-center gap-1 mt-4 overflow-x-auto">
                                        {[
                                            { id: 'profile', label: 'Profile', icon: Users },
                                            { id: 'courses', label: 'Courses', icon: BookOpen },
                                            { id: 'performance', label: 'Performance', icon: TrendingUp },
                                            { id: 'activity', label: 'Activity', icon: Clock },
                                        ].map(tab => {
                                            const Icon = tab.icon
                                            return (
                                                <button
                                                    key={tab.id}
                                                    onClick={() => setDetailTab(tab.id as DetailTab)}
                                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${detailTab === tab.id
                                                        ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25'
                                                        : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                >
                                                    <Icon className="h-4 w-4" />
                                                    {tab.label}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>

                                {/* Modal Content */}
                                <div className="flex-1 overflow-y-auto p-6">
                                    {detailTab === 'profile' && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Mail className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Email</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.email || 'Not provided'}</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Phone className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Phone</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.contactNumber || 'Not provided'}</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Target className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Target Year</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">NEET {selectedStudent.targetYear}</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Calendar className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Batch</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.batch}</p>
                                                </div>
                                            </div>
                                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                <h4 className="text-sm font-semibold text-theme-primary mb-2">Weak Subjects</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {selectedStudent.weakSubjects.length > 0 ? selectedStudent.weakSubjects.map(subj => (
                                                        <span key={subj} className="px-3 py-1 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-medium flex items-center gap-1">
                                                            <AlertTriangle className="h-3 w-3" /> {subj}
                                                        </span>
                                                    )) : (
                                                        <span className="text-sm text-emerald-600 font-medium">No weak subjects — performing well across all!</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {detailTab === 'courses' && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-semibold text-theme-primary">Enrolled Courses</h4>
                                                <button
                                                    onClick={() => setShowAddCourse(true)}
                                                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-primary-blue text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                >
                                                    <Plus className="h-3.5 w-3.5" /> Add Course
                                                </button>
                                            </div>

                                            {getStudentCourses(selectedStudent.id).length > 0 ? (
                                                <div className="space-y-3">
                                                    {getStudentCourses(selectedStudent.id).map(courseId => {
                                                        const course = mockAdminCourses.find(c => c.id === courseId)
                                                        if (!course) return null
                                                        return (
                                                            <div key={courseId} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl flex items-center justify-between gap-4">
                                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shrink-0">
                                                                        <BookOpen className="h-5 w-5 text-white" />
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <p className="font-semibold text-theme-primary text-sm truncate">{course.title}</p>
                                                                        <div className="flex items-center gap-2 mt-0.5">
                                                                            <span className="text-xs text-theme-secondary">{course.type}</span>
                                                                            <span className="text-xs text-theme-muted">•</span>
                                                                            <span className="text-xs text-theme-secondary">{course.duration}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2 shrink-0">
                                                                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium">Enrolled</span>
                                                                    <button
                                                                        onClick={() => handleRemoveCourse(courseId)}
                                                                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                                        title="Remove course"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="p-8 text-center rounded-xl bg-slate-50 dark:bg-slate-800/50">
                                                    <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                                                    <p className="text-theme-secondary text-sm">No courses enrolled yet</p>
                                                    <button
                                                        onClick={() => setShowAddCourse(true)}
                                                        className="mt-3 text-sm text-primary-blue font-semibold hover:underline"
                                                    >
                                                        Add a course →
                                                    </button>
                                                </div>
                                            )}

                                            {/* Add Course Dropdown */}
                                            <AnimatePresence>
                                                {showAddCourse && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="p-4 border-2 border-dashed border-primary-blue/30 rounded-xl bg-blue-50/50 dark:bg-blue-900/10">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h5 className="text-sm font-semibold text-theme-primary">Select Course to Enroll</h5>
                                                                <button onClick={() => setShowAddCourse(false)} className="p-1 text-theme-secondary hover:text-theme-primary">
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                                {mockAdminCourses.filter(c => !getStudentCourses(selectedStudent.id).includes(c.id)).map(course => (
                                                                    <button
                                                                        key={course.id}
                                                                        onClick={() => handleAddCourse(course.id)}
                                                                        className="w-full p-3 text-left bg-white dark:bg-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-between"
                                                                    >
                                                                        <div>
                                                                            <p className="text-sm font-medium text-theme-primary">{course.title}</p>
                                                                            <p className="text-xs text-theme-secondary">{course.type} · {course.price}</p>
                                                                        </div>
                                                                        <Plus className="h-4 w-4 text-primary-blue" />
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )}

                                    {detailTab === 'performance' && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-center">
                                                    <Award className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                                                    <p className="text-2xl font-bold text-theme-primary">{selectedStudent.avgTestScore}</p>
                                                    <p className="text-xs text-theme-secondary">Avg Score / 720</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-center">
                                                    <Target className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                                                    <p className="text-2xl font-bold text-theme-primary">{selectedStudent.disciplineScore}</p>
                                                    <p className="text-xs text-theme-secondary">Discipline / 10</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-center">
                                                    <Clock className="h-5 w-5 text-violet-500 mx-auto mb-1" />
                                                    <p className="text-2xl font-bold text-theme-primary">{selectedStudent.weeklyAvgHours}h</p>
                                                    <p className="text-xs text-theme-secondary">Weekly Avg</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-center">
                                                    <Flame className="h-5 w-5 text-orange-500 mx-auto mb-1" />
                                                    <p className="text-2xl font-bold text-theme-primary">{selectedStudent.currentStreak}</p>
                                                    <p className="text-xs text-theme-secondary">Day Streak</p>
                                                </div>
                                            </div>

                                            {/* Performance Trend */}
                                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                <h4 className="text-sm font-semibold text-theme-primary mb-3">Performance Trend</h4>
                                                <div className="flex items-center gap-4">
                                                    <div className={`text-3xl font-bold flex items-center gap-1 ${selectedStudent.trendValue > 0 ? 'text-emerald-600' : selectedStudent.trendValue < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                                                        {selectedStudent.trendValue > 0 ? <ArrowUpRight className="h-6 w-6" /> : selectedStudent.trendValue < 0 ? <ArrowDownRight className="h-6 w-6" /> : null}
                                                        {selectedStudent.trendValue > 0 ? '+' : ''}{selectedStudent.trendValue}%
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-theme-primary capitalize">{selectedStudent.performanceTrend.replace('-', ' ')}</p>
                                                        <p className="text-xs text-theme-secondary">Compared to last month</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Score Bar */}
                                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                <h4 className="text-sm font-semibold text-theme-primary mb-3">Test Score Progress</h4>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-theme-secondary">Score</span>
                                                        <span className="font-bold text-theme-primary">{selectedStudent.avgTestScore} / 720</span>
                                                    </div>
                                                    <div className="h-4 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-1000 ${selectedStudent.avgTestScore >= 600 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                                                                selectedStudent.avgTestScore >= 500 ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
                                                                    selectedStudent.avgTestScore >= 400 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                                                                        'bg-gradient-to-r from-red-400 to-red-500'
                                                                }`}
                                                            style={{ width: `${(selectedStudent.avgTestScore / 720) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {detailTab === 'activity' && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Clock className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Last Activity</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.lastActivity}</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Flame className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Current Streak</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.currentStreak} days</p>
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <Calendar className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Today's Hours</span>
                                                    </div>
                                                    {selectedStudent.todayHoursSubmitted ? (
                                                        <p className="text-sm font-semibold text-emerald-600">{selectedStudent.todayHoursReported}h submitted ✓</p>
                                                    ) : (
                                                        <p className="text-sm font-semibold text-amber-600">Not submitted</p>
                                                    )}
                                                </div>
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <TrendingUp className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Weekly Average</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.weeklyAvgHours}h / day</p>
                                                </div>
                                            </div>

                                            {/* Course Context */}
                                            {selectedStudent.courseName && (
                                                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                    <div className="flex items-center gap-2 text-theme-secondary mb-1">
                                                        <BookOpen className="h-4 w-4" />
                                                        <span className="text-xs font-medium">Primary Course</span>
                                                    </div>
                                                    <p className="text-sm font-semibold text-theme-primary">{selectedStudent.courseName}</p>
                                                </div>
                                            )}

                                            {/* Discipline History Visual */}
                                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                                                <h4 className="text-sm font-semibold text-theme-primary mb-3">Discipline Score</h4>
                                                <div className="flex items-center gap-3">
                                                    <div className={`text-3xl font-bold ${selectedStudent.disciplineScore >= 8 ? 'text-emerald-600' : selectedStudent.disciplineScore >= 6 ? 'text-blue-600' : selectedStudent.disciplineScore >= 4 ? 'text-amber-600' : 'text-red-600'}`}>
                                                        {selectedStudent.disciplineScore}/10
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full ${selectedStudent.disciplineScore >= 8 ? 'bg-emerald-500' : selectedStudent.disciplineScore >= 6 ? 'bg-blue-500' : selectedStudent.disciplineScore >= 4 ? 'bg-amber-500' : 'bg-red-500'}`}
                                                                style={{ width: `${selectedStudent.disciplineScore * 10}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}
